import { useState } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { Flex, Text } from '@chakra-ui/react';
import { Box } from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Modal, TextArea } from '@morressier/alembic';
import { rescindReviewSchema } from '@publishing-engine/domain/validation';
import { IReviewer, ISubmissionFile } from '@publishing-engine/domain/models';
import { deleteUploadedFile } from 'src/api/queries';
import { faWarning } from '@fortawesome/pro-light-svg-icons';
import { FieldLabel } from '../Field';
import { FormFieldMultipleFileUpload } from '../FormField';
import { MAX_FILE_SIZE_MB } from '../../constants';

type FileUploadType = ISubmissionFile & { isUploading: boolean };
export type RescindReviewFormValues = {
  feedback: string;
  attachments: FileUploadType[];
};
interface Props {
  isOpen: boolean;
  onClose: () => void;
  reviewer: IReviewer;
  submissionContextId: string;
  submissionId: string;
  onRescindReview: (formValues: RescindReviewFormValues) => void;
}

const RescindReviewModal: React.FC<Props> = props => {
  const {
    isOpen,
    onClose,
    reviewer,
    submissionId,
    submissionContextId,
    onRescindReview
  } = props;
  const [isLoading, setIsLoading] = useState(false);

  const methods = useForm<RescindReviewFormValues>({
    defaultValues: {
      feedback: '',
      attachments: []
    },
    resolver: zodResolver(rescindReviewSchema)
  });

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch
  } = methods;

  const { remove, fields } = useFieldArray({
    control,
    name: 'attachments'
  });

  const removeFile = async (index: number, fileId?: string) => {
    remove(index);
    fileId && deleteUploadedFile(fileId);
  };

  const handleSaveResponse = async (formValues: RescindReviewFormValues) => {
    setIsLoading(true);
    await onRescindReview(formValues);
    setIsLoading(false);
  };

  const handleCloseModal = () => {
    // delete all uploaded files before closing modal
    Promise.allSettled(
      fields.map((file, index) => removeFile(index, file.fileId))
    );
    reset();
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCloseModal}
      width="45rem"
      closeButtonProps={{
        hidden: true
      }}
    >
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(handleSaveResponse)}>
          <Flex flexDirection="column" gap={6} padding={8}>
            <Box>
              <FontAwesomeIcon fontSize={32} icon={faWarning} />
            </Box>
            <Flex flexDirection="column" gap={2}>
              <Flex alignItems="center">
                <Text textStyle="headingGroup">
                  Rescind Review from {reviewer.firstName} {reviewer.lastName}?
                </Text>
              </Flex>
              <Text textStyle="textBody">
                To rescind this Review, explain the reasons to the Reviewer to
                address any issues. Your feedback will emailed to the Reviewer,
                which will be recorded in their review.
              </Text>
            </Flex>

            <Flex gap={2} flexDirection="column">
              <Flex alignItems="center" gap={2}>
                <FieldLabel
                  label="Feedback to Reviewer"
                  subText=""
                  isRequired
                />
              </Flex>
              <TextArea
                value={watch('feedback')}
                {...register('feedback')}
                placeholder="Add a message to the reviewer"
                isInvalid={Boolean(errors.feedback)}
                errorMessage={errors?.feedback?.message}
                rows={4}
              />
            </Flex>

            <FormFieldMultipleFileUpload
              submissionId={submissionId}
              submissionContextId={submissionContextId}
              config={{
                name: 'attachments',
                params: {
                  validations: {
                    kind: 'fileArray',
                    maxSizeMB: MAX_FILE_SIZE_MB,
                    maxItems: 15,
                    allowedFileFormats: {
                      'application/pdf': ['.pdf'],
                      'application/msword': ['.doc'],
                      'application/vnd.openxmlformats': ['.docx']
                    },
                    required: false
                  },
                  labels: {
                    main: 'Attach Files',
                    subtitle: ''
                  }
                },
                caseDataFields: {
                  main: 'attachments'
                }
              }}
            />

            <Flex flexDirection="row" gap={2} justifyContent="end">
              <Button
                variant="secondary"
                onClick={handleCloseModal}
                isDisabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                type="submit"
                isDisabled={isLoading}
                isLoading={isLoading}
              >
                Rescind Review
              </Button>
            </Flex>
          </Flex>
        </form>
      </FormProvider>
    </Modal>
  );
};

export default RescindReviewModal;
